import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Sparkles,
} from "lucide-react";

const ProcessSection = ({ data }) => {
  if (!data) return null;

  return (
    <section
      className="
        relative
        overflow-hidden
        bg-[var(--bg-primary)]
        py-16

        sm:py-20

        lg:py-24
      "
    >
      {/* ============================================================
          BACKGROUND GLOW - LEFT
      ============================================================ */}

      <div
        className="
          absolute
          -left-28
          top-16
          h-[220px]
          w-[220px]
          rounded-full
          bg-orange-500/10
          blur-[120px]

          sm:h-[280px]
          sm:w-[280px]

          lg:-left-40
          lg:top-20
          lg:h-[350px]
          lg:w-[350px]
          lg:blur-[170px]
        "
      />

      {/* ============================================================
          BACKGROUND GLOW - RIGHT
      ============================================================ */}

      <div
        className="
          absolute
          right-0
          bottom-0
          h-[220px]
          w-[220px]
          rounded-full
          bg-orange-500/10
          blur-[120px]

          sm:h-[280px]
          sm:w-[280px]

          lg:h-[320px]
          lg:w-[320px]
          lg:blur-[170px]
        "
      />

      {/* ============================================================
          BACKGROUND GRID
      ============================================================ */}

      <div
        className="
          pointer-events-none
          absolute
          inset-0

          bg-[linear-gradient(rgba(0,0,0,.035)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,.035)_1px,transparent_1px)]

          dark:bg-[linear-gradient(rgba(255,255,255,.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.03)_1px,transparent_1px)]

          bg-[size:70px_70px]
        "
      />

      {/* ============================================================
          MAIN CONTAINER
      ============================================================ */}

      <div
        className="
          relative
          mx-auto
          max-w-7xl
          px-5

          sm:px-6

          lg:px-8
        "
      >
        {/* ==========================================================
            HEADING
        ========================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 40,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.7,
          }}
          className="
            mx-auto
            mb-12
            max-w-3xl
            text-center

            sm:mb-16

            lg:mb-20
          "
        >
          {/* ========================================================
              BADGE
          ======================================================== */}

          <span
            className="
              inline-flex
              items-center
              gap-2
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/10
              px-4
              py-2
              text-[11px]
              font-semibold
              uppercase
              tracking-[2px]
              text-[var(--brand-primary)]

              sm:px-5
              sm:text-xs
              sm:tracking-[3px]
            "
          >
            <Sparkles size={14} />

            Our Process
          </span>

          {/* ========================================================
              HEADING
          ======================================================== */}

          <h2
            className="
              mt-5
              text-3xl
              font-bold
              text-[var(--text-primary)]

              sm:text-4xl

              lg:mt-6
              lg:text-5xl
            "
          >
            {data.title}
          </h2>

          {/* ========================================================
              DESCRIPTION
          ======================================================== */}

          <p
            className="
              mx-auto
              mt-5
              max-w-3xl
              text-base
              leading-7
              text-[var(--text-muted)]

              sm:text-lg
              sm:leading-8

              lg:mt-6
            "
          >
            We follow a structured and transparent workflow to ensure
            every registration, certification, and compliance service
            is delivered efficiently with complete legal accuracy.
          </p>
        </motion.div>

        {/* ==========================================================
            TIMELINE
        ========================================================== */}

        <div className="relative">
          {/* ========================================================
              VERTICAL LINE
          ======================================================== */}

          <div
            className="
              absolute
              left-[22px]
              top-5
              hidden
              h-[calc(100%-40px)]
              w-[2px]

              bg-gradient-to-b
              from-[var(--brand-primary)]
              via-[#ffb454]
              to-[var(--brand-primary)]

              lg:block
            "
          />

          {/* ========================================================
              PROCESS STEPS
          ======================================================== */}

          <div
            className="
              space-y-5

              sm:space-y-6

              lg:space-y-8
            "
          >
            {data.steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{
                  opacity: 0,
                  y: 40,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.08,
                }}
                className="
                  relative
                  flex
                  items-start
                  gap-4

                  sm:gap-6

                  lg:gap-8
                "
              >
                {/* ==================================================
                    DESKTOP STEP NUMBER
                ================================================== */}

                <motion.div
                  whileHover={{
                    scale: 1.06,
                  }}
                  className="
                    relative
                    z-20
                    hidden

                    lg:flex
                  "
                >
                  <div
                    className="
                      flex
                      h-12
                      w-12
                      items-center
                      justify-center
                      rounded-xl
                      border
                      border-orange-500/30
                      bg-gradient-to-br
                      from-[var(--brand-primary)]
                      to-[#ff9d23]
                      text-base
                      font-bold
                      text-white
                      shadow-[0_12px_30px_rgba(255,136,0,.35)]

                      xl:h-14
                      xl:w-14
                      xl:text-lg
                    "
                  >
                    {String(index + 1).padStart(2, "0")}
                  </div>
                </motion.div>

                {/* ==================================================
                    PROCESS CARD
                ================================================== */}

                <motion.div
                  whileHover={{
                    x: 6,
                  }}
                  className="
                    group
                    relative
                    flex-1
                    overflow-hidden
                    rounded-2xl
                    border
                    border-[var(--border-primary)]
                    bg-[var(--card-bg-secondary)]
                    transition-all
                    duration-500

                    lg:rounded-3xl

                    hover:border-orange-500/40
                    hover:shadow-[0_20px_60px_rgba(255,136,0,.12)]
                  "
                >
                  {/* ==================================================
                      LEFT ACCENT
                  ================================================== */}

                  <div
                    className="
                      absolute
                      left-0
                      top-0
                      h-full
                      w-1
                      bg-gradient-to-b
                      from-[var(--brand-primary)]
                      via-[#ff9d23]
                      to-[var(--brand-primary)]
                      opacity-0
                      transition-all
                      duration-500

                      group-hover:opacity-100
                    "
                  />

                  {/* ==================================================
                      CARD CONTENT
                  ================================================== */}

                  <div
                    className="
                      flex
                      items-center
                      justify-between
                      gap-4
                      px-5
                      py-5

                      sm:px-6
                      sm:py-6

                      lg:gap-8
                      lg:px-8
                      lg:py-7
                    "
                  >
                    {/* ==================================================
                        LEFT CONTENT
                    ================================================== */}

                    <div className="flex-1">
                      <div
                        className="
                          flex
                          items-start
                          gap-3

                          sm:gap-4
                        "
                      >
                        {/* ==================================================
                            MOBILE STEP NUMBER
                        ================================================== */}

                        <div
                          className="
                            flex
                            h-10
                            w-10
                            shrink-0
                            items-center
                            justify-center
                            rounded-xl
                            border
                            border-orange-500/30
                            bg-gradient-to-br
                            from-[var(--brand-primary)]
                            to-[#ff9d23]
                            text-sm
                            font-bold
                            text-white

                            lg:hidden
                          "
                        >
                          {String(index + 1).padStart(2, "0")}
                        </div>

                        {/* ==================================================
                            ICON
                        ================================================== */}

                        <div
                          className="
                            hidden
                            h-11
                            w-11
                            shrink-0
                            items-center
                            justify-center
                            rounded-xl
                            bg-orange-500/10
                            transition-all
                            duration-300

                            sm:flex

                            lg:h-12
                            lg:w-12

                            group-hover:bg-[var(--brand-primary)]
                          "
                        >
                          <CheckCircle2
                            size={20}
                            className="
                              text-[var(--brand-primary)]
                              transition-all
                              duration-300

                              group-hover:text-white
                            "
                          />
                        </div>

                        {/* ==================================================
                            TEXT CONTENT
                        ================================================== */}

                        <div className="flex-1">
                          {/* ==================================================
                              STEP TITLE
                          ================================================== */}

                          <h3
                            className="
                              text-lg
                              font-semibold
                              text-[var(--text-primary)]
                              transition-colors
                              duration-300

                              sm:text-xl

                              lg:text-2xl

                              group-hover:text-orange-400
                            "
                          >
                            {step.title}
                          </h3>

                          {/* ==================================================
                              STEP DESCRIPTION
                          ================================================== */}

                          <p
                            className="
                              mt-3
                              text-sm
                              leading-7
                              text-[var(--text-muted)]

                              sm:mt-4
                              sm:text-[15px]

                              lg:mt-5
                              lg:text-base
                              lg:leading-8
                            "
                          >
                            {step.description}
                          </p>

                          {/* ==================================================
                              BOTTOM ACCENT
                          ================================================== */}

                          <div
                            className="
                              mt-4
                              h-[3px]
                              w-14
                              rounded-full
                              bg-gradient-to-r
                              from-[var(--brand-primary)]
                              to-[#ffb347]
                              transition-all
                              duration-500

                              sm:mt-5

                              lg:mt-6
                              lg:w-16

                              group-hover:w-24
                              lg:group-hover:w-28
                            "
                          />
                        </div>
                      </div>
                    </div>

                    {/* ==================================================
                        ARROW
                    ================================================== */}

                    <motion.div
                      whileHover={{
                        x: 4,
                      }}
                      className="
                        hidden
                        h-12
                        w-12
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        border
                        border-[var(--border-primary)]
                        bg-[var(--bg-tertiary)]
                        transition-all
                        duration-300

                        lg:flex

                        xl:h-14
                        xl:w-14

                        group-hover:border-orange-500
                        group-hover:bg-[var(--brand-primary)]
                      "
                    >
                      <ArrowRight
                        size={20}
                        className="
                          text-[var(--text-secondary)]
                          transition-all
                          duration-300

                          group-hover:translate-x-1
                          group-hover:text-white
                        "
                      />
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;